// Strip Card value sets and shapes (ISSUE-1285). v1 kept one client-written
// document per pool and game with a map of sets s1..sN; v2 keeps the same
// card shape but the server makes every decision. A card belongs to one game
// and holds up to four strips. A strip has ten rows. A member claims a row;
// when an admin locks the strip the server draws one digit 0-9 per row, and
// an admin marks the winning rows by hand.

import type { typeEpochMs } from "../domain/primitives";

/** Rows on one strip. One digit per row once the strip is locked. */
export const STRIP_ROW_COUNT = 10;

/** Strips one card may hold. v1's "+" disappeared at four. */
export const STRIP_MAX_STRIPS = 4;

/**
 * The admin's choices for spots one member may hold on one strip. Null is
 * Unlimited. v1 stored "0" for Unlimited and fell back to 4 when blank; v2
 * has no blank: a pool always carries a choice from this list or null.
 */
export const STRIP_MAX_SPOTS_CHOICES = [1, 2, 3, 4, 5] as const;
export type typeStripMaxSpotsChoice = (typeof STRIP_MAX_SPOTS_CHOICES)[number];
export type typeStripMaxSpotsPerMember = typeStripMaxSpotsChoice | null;

/** The create step's default: v1's legacy cap. */
export const STRIP_DEFAULT_MAX_SPOTS_PER_MEMBER: typeStripMaxSpotsPerMember = 4;

/** Open takes claims. Locked has its digits drawn and takes winners. */
export const STRIP_STATES = ["open", "locked"] as const;
export type typeStripState = (typeof STRIP_STATES)[number];

/** A winner's note. v1's dialog was a 5-8 line text field. */
export const STRIP_WINNER_NOTE_MAX_LENGTH = 500;

/** Winners one row may carry (one per quarter and then some). */
export const STRIP_MAX_WINNERS_PER_ROW = 10;

/** What the Commissioner chose on the create step. */
export type typeStripCardSetup = {
  /** The first game. Its card is created with strip 1 at bootstrap. */
  eventId: string;
  maxSpotsPerMember: typeStripMaxSpotsPerMember;
};

/** One row of one strip. Row 0 is the top row, shown to fans as "1". */
export type typeStripRow = {
  row: number;
  /** Null is an open row. */
  userId: string | null;
  /** Who put the member there: the member on a claim, the admin on an assign. */
  assignedByUserId: string | null;
  assignedAtMs: typeEpochMs | null;
};

export type typeStrip = {
  /** 1 to 4. Strip n is stored at index n - 1 and is never removed. */
  stripNumber: number;
  state: typeStripState;
  /** Ten digits, a permutation of 0..9 in row order, null while open. */
  digits: readonly number[] | null;
  rows: readonly typeStripRow[];
  lockedAtMs: typeEpochMs | null;
};

/** Everything the card rules read about one card. */
export type typeStripCardFacts = {
  isCanceled: boolean;
  strips: readonly typeStrip[];
};

/** One active winner on a row, as the winner rules read it. */
export type typeStripWinnerFact = {
  stripWinnerId: string;
  stripNumber: number;
  row: number;
  /** Snapshot of the row's digit when the winner was set. */
  digit: number;
  /** Snapshot of who held the row when the winner was set. Null is an open row. */
  userId: string | null;
  wonAtMs: typeEpochMs;
  note: string;
};

/** One entry of the admin's winner sheet. Null id is a new winner. */
export type typeStripWinnerInput = {
  stripWinnerId: string | null;
  wonAtMs: typeEpochMs;
  note: string;
};

/**
 * Why a Strip Card action was refused. contest-api maps each one to an error
 * code (service-design.md, "Refusals").
 */
export const STRIP_REFUSALS = [
  "gameCanceled",
  "noSuchStrip",
  "noSuchRow",
  "stripLocked",
  "stripNotLocked",
  "spotTaken",
  "notYourSpot",
  "maxSpotsReached",
  "stripLimitReached",
  "sameStrip",
  "tooManyWinners",
  "noteTooLong",
] as const;
export type typeStripRefusal = (typeof STRIP_REFUSALS)[number];

export type typeStripDecision<TOk extends object = object> =
  ({ ok: true } & TOk) | { ok: false; reason: typeStripRefusal };
